import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes without style conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Common style patterns used throughout the application
 */
export const styles = {
  // Layout styles
  layout: {
    flexRow: 'flex flex-row min-h-screen bg-gray-100 dark:bg-gray-900',
    flexCol: 'flex flex-col',
    center: 'flex items-center justify-center',
    between: 'flex items-center justify-between',
    fixed: 'fixed inset-y-0 left-0',
    hidden: 'hidden',
  },

  // Text styles
  text: {
    h1: 'text-4xl font-bold tracking-tight',
    h2: 'text-3xl font-semibold tracking-tight',
    h3: 'text-2xl font-semibold tracking-tight',
    h4: 'text-xl font-semibold tracking-tight',
    p: 'text-base leading-7',
    small: 'text-sm',
    muted: 'text-gray-500 dark:text-gray-400',
  },

  // Form styles
  form: {
    label: 'block text-sm font-medium text-gray-700 dark:text-gray-300',
    input: 'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500',
    error: 'mt-2 text-sm text-red-600 dark:text-red-500',
    hint: 'mt-2 text-sm text-gray-500 dark:text-gray-400',
  },

  // Container styles
  container: {
    base: 'mx-auto px-4 sm:px-6 lg:px-8',
    sm: 'max-w-screen-sm',
    md: 'max-w-screen-md',
    lg: 'max-w-screen-lg',
    xl: 'max-w-screen-xl',
    '2xl': 'max-w-screen-2xl',
    full: 'max-w-full',
  },

  // Animation styles
  animation: {
    fadeIn: 'animate-fadeIn',
    slideIn: 'animate-slideIn',
    spin: 'animate-spin',
    pulse: 'animate-pulse',
  },

  // Transition styles
  transition: {
    fast: 'transition-all duration-150',
    normal: 'transition-all duration-200',
    slow: 'transition-all duration-300',
    transform: 'transition-transform',
  },

  // Focus styles
  focus: {
    ring: 'focus:outline-none focus:ring-2 focus:ring-offset-2',
    within: 'focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2',
    visible: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    ringPrimary: 'focus:ring-primary-500 dark:focus:ring-primary-400',
    ringOffset: 'focus:ring-offset-white dark:focus:ring-offset-gray-900',
  },

  // State styles
  states: {
    hover: 'hover:bg-gray-100 dark:hover:bg-gray-800',
    active: 'active:bg-gray-200 dark:active:bg-gray-700',
    disabled: 'opacity-50 cursor-not-allowed',
  },

  // Color styles
  colors: {
    primary: {
      light: 'text-primary-600 dark:text-primary-400',
      hover: 'hover:text-primary-700 dark:hover:text-primary-300',
      bg: 'bg-primary-600 dark:bg-primary-500',
      text: 'text-primary-600 dark:text-primary-400',
    },
    gray: {
      light: 'text-gray-600 dark:text-gray-400',
      hover: 'hover:text-gray-700 dark:hover:text-gray-300',
      bg: 'bg-gray-100 dark:bg-gray-800',
    },
  },

  // Transform styles
  transform: {
    rotate180: 'rotate-180',
    rotateNeg180: '-rotate-180',
    translateX: {
      none: 'translate-x-0',
      full: 'translate-x-full',
      neg: '-translate-x-full',
    },
  },

  // Sidebar specific styles
  sidebar: {
    base: 'w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700',
    collapsed: 'w-16',
    nav: 'flex-1 space-y-1 px-2',
    header: 'h-16 px-4 border-b border-gray-200 dark:border-gray-700',
    footer: 'p-4 border-t border-gray-200 dark:border-gray-700',
    overlay: 'fixed inset-0 bg-gray-600 bg-opacity-75',
  },
};

/**
 * Example usage:
 * 
 * import { cn, styles } from '@/lib/utils/styles';
 * 
 * function Component({ className }: { className?: string }) {
 *   return (
 *     <div className={cn(
 *       styles.layout.flexCol,
 *       styles.container.base,
 *       styles.container.md,
 *       className
 *     )}>
 *       <h1 className={styles.text.h1}>Title</h1>
 *       <p className={cn(
 *         styles.text.p,
 *         styles.text.muted,
 *         styles.transition.normal
 *       )}>
 *         Content
 *       </p>
 *     </div>
 *   );
 * }
 */
